// Location Page Configuration - CMS Ready
// This data structure makes it easy to scale to new locations

export interface LocationPageData {
  slug: string;
  slugEn: string;
  city: string;
  region: string;
  country: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  isPrimary: boolean;
  
  // SEO
  titleEs: string;
  titleEn: string;
  descriptionEs: string;
  descriptionEn: string;
  keywordsEs: string[];
  keywordsEn: string[];
  
  // Content
  heroTitleEs: string;
  heroTitleEn: string;
  heroSubtitleEs: string;
  heroSubtitleEn: string;
  
  whyLocalEs: string;
  whyLocalEn: string;
  
  localChallengesEs: string[];
  localChallengesEn: string[];
  
  servicesEs: {
    title: string;
    description: string;
  }[];
  servicesEn: {
    title: string;
    description: string;
  }[];
  
  faqsEs: {
    question: string;
    answer: string;
  }[];
  faqsEn: {
    question: string;
    answer: string;
  }[];
}

export const houstonLocationData: LocationPageData = {
  slug: "seo-local-houston",
  slugEn: "local-seo-houston",
  city: "Houston",
  region: "Texas",
  country: "US",
  coordinates: {
    latitude: 29.7604,
    longitude: -95.3698
  },
  isPrimary: true,
  
  // SEO
  titleEs: "Consultor SEO Local en Houston para Empresas B2B | Luis ROC",
  titleEn: "Local SEO Consultant in Houston for B2B Companies | Luis ROC",
  descriptionEs: "Servicios de SEO local en Houston para empresas B2B. Optimización de Google Business Profile, posicionamiento en búsquedas locales y generación de leads calificados.",
  descriptionEn: "Local SEO services in Houston for B2B companies. Google Business Profile optimization, local search positioning and qualified lead generation.",
  keywordsEs: [
    "SEO local Houston",
    "consultor SEO Houston",
    "SEO empresas B2B Houston",
    "Google Business Profile Houston",
    "posicionamiento local Houston",
    "SEO Houston Texas"
  ],
  keywordsEn: [
    "local SEO Houston",
    "SEO consultant Houston",
    "B2B SEO Houston",
    "Google Business Profile Houston",
    "local positioning Houston",
    "SEO Houston Texas"
  ],
  
  // Content
  heroTitleEs: "Consultor SEO Local en Houston para empresas B2B",
  heroTitleEn: "Local SEO Consultant in Houston for B2B Companies",
  heroSubtitleEs: "Posiciona tu empresa en las búsquedas locales de Houston. Experiencia real con negocios B2B del área metropolitana.",
  heroSubtitleEn: "Position your company in Houston's local searches. Real experience with B2B businesses in the metropolitan area.",
  
  whyLocalEs: "Houston es el cuarto mercado más grande de Estados Unidos y uno de los más competitivos para búsquedas B2B. Miles de fabricantes, distribuidores y proveedores de servicios compiten por visibilidad en Google Maps y resultados locales. Sin una estrategia de SEO local, tu empresa puede estar perdiendo oportunidades frente a competidores que sí invierten en posicionamiento.",
  whyLocalEn: "Houston is the fourth largest market in the United States and one of the most competitive for B2B searches. Thousands of manufacturers, distributors and service providers compete for visibility in Google Maps and local results. Without a local SEO strategy, your company may be losing opportunities to competitors who do invest in positioning.",
  
  localChallengesEs: [
    "Alta competencia en búsquedas B2B locales",
    "Múltiples zonas de servicio: Downtown, Energy Corridor, Galleria, Heights",
    "Mercado hispanohablante significativo que requiere estrategia bilingüe",
    "Industrias clave: energía, manufactura, salud, legal, construcción"
  ],
  localChallengesEn: [
    "High competition in local B2B searches",
    "Multiple service areas: Downtown, Energy Corridor, Galleria, Heights",
    "Significant Spanish-speaking market requiring bilingual strategy",
    "Key industries: energy, manufacturing, healthcare, legal, construction"
  ],
  
  servicesEs: [
    {
      title: "Optimización de Google Business Profile",
      description: "Perfil completamente optimizado con categorías correctas, atributos, publicaciones regulares y gestión de reseñas."
    },
    {
      title: "SEO técnico local",
      description: "Estructura web optimizada para búsquedas locales, Schema markup geográfico, y velocidad de carga mejorada."
    },
    {
      title: "Contenido por intención local",
      description: "Páginas de servicio optimizadas para Houston y sus áreas circundantes con keywords de intención comercial."
    },
    {
      title: "Construcción de autoridad local",
      description: "Citaciones NAP consistentes, presencia en directorios relevantes, y estrategia de reseñas."
    }
  ],
  servicesEn: [
    {
      title: "Google Business Profile Optimization",
      description: "Fully optimized profile with correct categories, attributes, regular posts and review management."
    },
    {
      title: "Local Technical SEO",
      description: "Web structure optimized for local searches, geographic Schema markup, and improved loading speed."
    },
    {
      title: "Local Intent Content",
      description: "Service pages optimized for Houston and surrounding areas with commercial intent keywords."
    },
    {
      title: "Local Authority Building",
      description: "Consistent NAP citations, presence in relevant directories, and review strategy."
    }
  ],
  
  faqsEs: [
    {
      question: "¿Qué áreas de Houston cubres?",
      answer: "Atiendo clientes en todo el área metropolitana de Houston incluyendo: Downtown, Galleria, Energy Corridor, Heights, Montrose, Memorial, Midtown, y áreas circundantes como The Woodlands, Spring, Katy, Sugar Land, y Conroe."
    },
    {
      question: "¿Cuánto tiempo toma posicionar en Houston?",
      answer: "Debido a la alta competencia en Houston, los resultados de SEO local pueden comenzar a verse entre 3 y 6 meses. Industrias menos competitivas o áreas específicas pueden ver mejoras más rápido."
    },
    {
      question: "¿Trabajas con estrategias bilingües?",
      answer: "Sí. Houston tiene una comunidad hispanohablante significativa. Podemos optimizar tu presencia tanto en inglés como en español para capturar ambos mercados."
    },
    {
      question: "¿Qué industrias atiendes en Houston?",
      answer: "Trabajo principalmente con empresas B2B: fabricantes industriales, distribuidores, servicios profesionales (legal, contable, consultoría), salud, y construcción."
    }
  ],
  faqsEn: [
    {
      question: "What areas of Houston do you cover?",
      answer: "I serve clients throughout the Houston metropolitan area including: Downtown, Galleria, Energy Corridor, Heights, Montrose, Memorial, Midtown, and surrounding areas like The Woodlands, Spring, Katy, Sugar Land, and Conroe."
    },
    {
      question: "How long does it take to rank in Houston?",
      answer: "Due to high competition in Houston, local SEO results can begin to show between 3 and 6 months. Less competitive industries or specific areas may see improvements faster."
    },
    {
      question: "Do you work with bilingual strategies?",
      answer: "Yes. Houston has a significant Spanish-speaking community. We can optimize your presence in both English and Spanish to capture both markets."
    },
    {
      question: "What industries do you serve in Houston?",
      answer: "I primarily work with B2B companies: industrial manufacturers, distributors, professional services (legal, accounting, consulting), healthcare, and construction."
    }
  ]
};

export const getLocationData = (slug: string): LocationPageData | null => {
  const locations: Record<string, LocationPageData> = {
    "seo-local-houston": houstonLocationData,
    "local-seo-houston": houstonLocationData
  };
  
  return locations[slug] || null;
};
